import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IWindowService } from 'angular';
import moment from 'moment';
import { DateRangeSelectorService } from '../date-range-selector/date-range-selector.service';

@Component({
    selector: 'cv-time-slider',
    templateUrl: './time-slider.component.html',
    styleUrls: ['./time-slider.component.scss']
})
export class TimeSliderComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() pageName: string;
    @Input() startTime = 0;
    @Output() startTimeChange = new EventEmitter<number>();
    @Input() endTime = 24;
    @Output() endTimeChange = new EventEmitter<number>();
    @Input() updateOn: boolean;
    @Input() isDisabled: boolean;
    @ViewChild('slider', { read: ElementRef }) slider: ElementRef<HTMLDivElement>;
    isStartRange = false;
    isEndRange = false;

    _sliderModel: number[] = [ this.startTime, this.endTime ];
    get sliderModel() {
        if (this._sliderModel[0] !== this.startTime || this._sliderModel[1] !== this.endTime) {
            this._sliderModel = [ this.startTime, this.endTime ]; 
        }

        return this._sliderModel;
    };
    set sliderModel (val) {
        if (this.startTime !== val[0]) {
            this.startTime = val[0];
            this.onValuesChanged();
        }
        if (this.endTime !== val[1]) {
            this.endTime = val[1];
            this.onValuesChanged();
        }
        this._sliderModel = val;
    }

    config: any;
    _saveDebounce: number = null;
    lastEmittedStartTime: number;
    lastEmittedEndTime: number;

    constructor(
        @Inject('$window') public $window: IWindowService,
        datePipe: DatePipe,
        private service: DateRangeSelectorService,
    ) {


        const dateFormatter = {
            to: val => {
                const hour = Math.floor(val);
                const minutes = Math.floor((val - hour) * 60);
                let timeHolder = new Date();
                timeHolder.setHours(hour);
                timeHolder.setMinutes(minutes);
                return datePipe.transform(timeHolder, $window.CVAngularTimeFormat);
            },
            from: val => {
                return moment(val, $window.CVMomentTimeFormat).hours();
            }
        }

        this.config = {
            connect: true,
            range: {
                min: 0, // midnight 
                max: 24, // midnight (next day)
            },
            margin: 0.5, // 30 min is min (for now)
            step: .25, // 15 min intervals
            tooltips: [dateFormatter, dateFormatter],
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.startTime) {
            this.lastEmittedStartTime = this.startTime; 
        }

        if (changes.endTime) {
            this.lastEmittedEndTime = this.endTime; 
        }
    }

    ngOnInit() {

    }

    ngAfterViewInit() {    
        requestAnimationFrame(() => {
            this.mergeTooltips(this.slider.nativeElement.children[0], 15, ' - ');
            this.updateSliderClasses();
        });
    }

    onValuesChanged() {
        if (this._saveDebounce != null) {
            clearTimeout(this._saveDebounce);
            this._saveDebounce = null;
        }
        this.updateSliderClasses();
        this._saveDebounce = this.$window.setTimeout(() => {
            if (this.lastEmittedStartTime != this.startTime) {
                this.lastEmittedStartTime = this.startTime;
                this.startTimeChange.emit(this.startTime);
            }

            if (this.lastEmittedEndTime != this.endTime) {
                this.lastEmittedEndTime = this.endTime;
                this.endTimeChange.emit(this.endTime);
            }

            this.service.saveTimeSettings(this.pageName, !this.isDisabled, this.startTime * 60, this.endTime * 60);
            this._saveDebounce = null;
        }, 500);
    }

    /**
    * @param slider HtmlElement with an initialized slider
    * @param threshold Minimum proximity (in percentages) to merge tooltips
    * @param separator String joining tooltips
    */
    mergeTooltips(slider, threshold: number, separator: string) {

        var textIsRtl = getComputedStyle(slider).direction === 'rtl';
        var isRtl = slider.noUiSlider?.options.direction === 'rtl';
        var isVertical = slider.noUiSlider?.options.orientation === 'vertical';
        var tooltips = slider.noUiSlider?.getTooltips();
        var origins = slider.noUiSlider?.getOrigins();
        var tooltipOptions = slider.noUiSlider?.options.tooltips;

        // Move tooltips into the origin element. The default stylesheet handles this.
        tooltips?.forEach(function (tooltip, index) {
            if (tooltip) {
                origins[index].appendChild(tooltip);
            }
        });

        slider.noUiSlider?.on('update', function (values, handle, unencoded, tap, positions) {

            var pools = [[]];
            var poolPositions = [[]];
            var poolValues = [[]];
            var atPool = 0;

            // Assign the first tooltip to the first pool, if the tooltip is configured
            if (tooltips[0]) {
                pools[0][0] = 0;
                poolPositions[0][0] = positions[0];
                poolValues[0][0] = values[0];
            }

            for (var i = 1; i < positions.length; i++) {
                if (!tooltips[i] || (positions[i] - positions[i - 1]) > threshold) {
                    atPool++;
                    pools[atPool] = [];
                    poolValues[atPool] = [];
                    poolPositions[atPool] = [];
                }

                if (tooltips[i]) {
                    pools[atPool].push(i);
                    poolValues[atPool].push(values[i]);
                    poolPositions[atPool].push(positions[i]);
                }
            }

            pools.forEach(function (pool, poolIndex) {
                var handlesInPool = pool.length;

                for (var j = 0; j < handlesInPool; j++) {
                    var handleNumber = pool[j];

                    if (j === handlesInPool - 1) {
                        var offset = 0;

                        poolPositions[poolIndex].forEach(function (value) {
                            offset += 1000 - 10 * value;
                        });

                        var direction = isVertical ? 'bottom' : 'right';
                        var last = isRtl ? 0 : handlesInPool - 1;
                        var lastOffset = 1000 - 10 * poolPositions[poolIndex][last];
                        offset = (textIsRtl && !isVertical ? 100 : 0) + (offset / handlesInPool) - lastOffset;

                        // Center this tooltip over the affected handles
                        if (typeof tooltipOptions[handleNumber]?.to === 'function') {
                            tooltips[handleNumber].innerHTML = poolValues[poolIndex].map(value => tooltipOptions[handleNumber].to(value)).join(separator);
                        } else {
                            tooltips[handleNumber].innerHTML = poolValues[poolIndex].join(separator);
                        }
                        tooltips[handleNumber].style.display = 'block';
                        tooltips[handleNumber].style[direction] = offset + '%';
                    } else {
                        // Hide this tooltip
                        tooltips[handleNumber].style.display = 'none';
                    }
                }
            });
        });
    }

     updateSliderClasses(): void {
        const sliderEl = this.slider?.nativeElement;

        this.isStartRange = (this.startTime >= 0 && this.startTime <= 2) && (this.endTime >= 0 && this.endTime <= 2);
         this.isEndRange = (this.endTime >= 23 && this.endTime <= 24) && (this.startTime >= 20 && this.startTime <= 24);

        if (sliderEl) {
            sliderEl.classList.toggle('at-start', this.isStartRange);
            sliderEl.classList.toggle('at-end', this.isEndRange);
        }
    }

}
